import React from 'react';
import CircuitBreakerStatus from '../CircuitBreakerStatus';
import WASelectList from '../WASelectList';
import VersionInfo from '../VersionInfo';
import { useTranslation } from '../../context/TranslationContext';
import { useWorldAnvilLists } from '../../hooks/useWorldAnvilLists';

export default function WorldTab() {
  const { t } = useTranslation();
  const {
    worlds,
    manuscripts,
    versions,
    tags,
    belTags,
    labels,
    hasValidUser,
    currentVersion
  } = useWorldAnvilLists();

  // Si pas d'utilisateur valide, afficher un message
  if (!hasValidUser) {
    return (
      <div className="wa-form-group">
        <CircuitBreakerStatus 
          route="/api/wa" 
          type="large"
          size="md"
        />
        
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-yellow-800">
            {t('CONFIG', 'NO_USER_SELECTED', 'No user selected. Please configure authentication first.')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wa-form-group">
      {/* Circuit Breaker Status for World Anvil API */}
      <CircuitBreakerStatus 
        route="/api/wa" 
        type="large"
        size="md"
      />

      {/* Choose your world */}
      <WASelectList
        label={t('CONFIG', 'CHOOSE_WORLD', 'Choose your world')}
        items={worlds.items}
        selectedValue={worlds.selectedValue}
        onSelect={worlds.selectItem}
        loading={worlds.loading}
        error={worlds.error}
      />

      {/* Choose your manuscript */}
      <WASelectList
        label={t('CONFIG', 'CHOOSE_MANUSCRIPT', 'Choose your manuscript')}
        items={manuscripts.items}
        selectedValue={manuscripts.selectedValue}
        onSelect={manuscripts.selectItem}
        loading={manuscripts.loading}
        error={manuscripts.error}
        disabled={!worlds.selectedValue}
      />

      {/* Choose your version */}
      <WASelectList
        label={t('CONFIG', 'CHOOSE_VERSION', 'Choose your version')}
        items={versions.items}
        selectedValue={versions.selectedValue}
        onSelect={versions.selectItem}
        loading={versions.loading}
        error={versions.error}
        disabled={!manuscripts.selectedValue}
      />

      {/* Current version info */}
      <VersionInfo 
        version={currentVersion} 
        belTags={belTags}
      />

      {/* Manuscript part labels */}
      <div>
        <h4 className="wa-label">
          {t('CONFIG', 'MANUSCRIPT_PART_LABELS', 'Manuscript part labels')} :
        </h4>
        <div className="space-y-3">
          <div 
            className="flex items-center space-x-3 p-3 rounded-md transition-colors"
            style={{ 
              backgroundColor: labels.draft.selectedValue?.color || 'transparent',
              border: labels.draft.selectedValue?.color ? '1px solid rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <label className="w-20 text-sm text-gray-600">{t('CONFIG', 'DRAFT', 'Draft')}</label>
            <select 
              className="flex-1 wa-select"
              value={labels.draft.selectedValue?.id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                if (selectedId) {
                  const selectedItem = labels.items.find(item => item.id === selectedId);
                  if (selectedItem) {
                    labels.draft.selectItem(selectedItem);
                  }
                }
              }}
              disabled={!manuscripts.selectedValue || labels.loading}
            >
              <option value="">
                {labels.loading 
                  ? t('CONFIG', 'LOADING', 'Loading...') 
                  : labels.error 
                    ? t('CONFIG', 'ERROR_LOADING', 'Error loading')
                    : t('CONFIG', 'SELECT_OPTION', 'Select option')
                }
              </option>
              {labels.items.map(label => (
                <option key={label.id} value={label.id}>
                  {label.title}
                </option>
              ))}
            </select>
          </div>
          <div 
            className="flex items-center space-x-3 p-3 rounded-md transition-colors"
            style={{ 
              backgroundColor: labels.canon.selectedValue?.color || 'transparent',
              border: labels.canon.selectedValue?.color ? '1px solid rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <label className="w-20 text-sm text-gray-600">{t('CONFIG', 'CANON', 'Canon')}</label>
            <select 
              className="flex-1 wa-select"
              value={labels.canon.selectedValue?.id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                if (selectedId) {
                  const selectedItem = labels.items.find(item => item.id === selectedId);
                  if (selectedItem) {
                    labels.canon.selectItem(selectedItem);
                  }
                }
              }}
              disabled={!manuscripts.selectedValue || labels.loading}
            >
              <option value="">
                {labels.loading 
                  ? t('CONFIG', 'LOADING', 'Loading...') 
                  : labels.error 
                    ? t('CONFIG', 'ERROR_LOADING', 'Error loading')
                    : t('CONFIG', 'SELECT_OPTION', 'Select option')
                }
              </option>
              {labels.items.map(label => (
                <option key={label.id} value={label.id}>
                  {label.title}
                </option>
              ))}
            </select>
          </div>
          <div 
            className="flex items-center space-x-3 p-3 rounded-md transition-colors"
            style={{ 
              backgroundColor: labels.copied.selectedValue?.color || 'transparent',
              border: labels.copied.selectedValue?.color ? '1px solid rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <label className="w-20 text-sm text-gray-600">{t('CONFIG', 'COPIED', 'Copied')}</label>
            <select 
              className="flex-1 wa-select"
              value={labels.copied.selectedValue?.id || ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                if (selectedId) {
                  const selectedItem = labels.items.find(item => item.id === selectedId);
                  if (selectedItem) {
                    labels.copied.selectItem(selectedItem);
                  }
                }
              }}
              disabled={!manuscripts.selectedValue || labels.loading}
            >
              <option value="">
                {labels.loading 
                  ? t('CONFIG', 'LOADING', 'Loading...') 
                  : labels.error 
                    ? t('CONFIG', 'ERROR_LOADING', 'Error loading')
                    : t('CONFIG', 'SELECT_OPTION', 'Select option')
                }
              </option>
              {labels.items.map(label => (
                <option key={label.id} value={label.id}>
                  {label.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        {labels.error && (
          <div className="text-red-600 text-sm mt-2">
            {t('CONFIG', 'ERROR', 'Error')}: {labels.error}
          </div>
        )}
      </div>
    </div>
  );
}
